import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSearch,
  FiX,
  FiMessageCircle,
  FiClock,
  FiArrowRight,
} from "react-icons/fi";
import { toast } from "react-toastify";
import messagingService from "../../services/messagingService";
import "./MessageSearchModal.css";

/**
 * 🔍 MessageSearchModal Component
 *
 * WhatsApp/Telegram-style message search with jump-to-message navigation
 *
 * Features:
 * - Full-text search across all conversations or specific conversation
 * - Click on result to jump to message with context
 * - Shows conversation name, timestamp, and message preview
 * - Infinite scroll for search results
 * - Debounced search for performance
 */
const MessageSearchModal = ({
  isOpen,
  onClose,
  onJumpToMessage,
  conversationId = null,
  conversationName = null,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNext: false,
  });
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setResults([]);
      setPagination({ currentPage: 1, totalPages: 1, hasNext: false });
    }
  }, [isOpen]);

  // Debounced search
  const performSearch = useCallback(
    async (searchQuery, page = 1) => {
      if (!searchQuery.trim()) {
        setResults([]);
        return;
      }

      // Require at least 2 characters for search
      if (searchQuery.trim().length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const response = await messagingService.searchMessages({
          query: searchQuery,
          conversationId: conversationId || undefined,
          page,
          limit: 20,
        });
if (response.success) {
          const messages = response.data.messages || [];
// Filter out deleted messages
          const filteredMessages = messages.filter(msg => !msg.isDeleted && msg.content);
setPagination({
            currentPage: response.data.pagination?.currentPage || 1,
            totalPages: response.data.pagination?.totalPages || 1,
            hasNext: response.data.pagination?.hasNext || false,
          });

          if (page === 1) {
            setResults(filteredMessages);
          } else {
            setResults((prev) => [...prev, ...filteredMessages]);
          }
        }
      } catch (error) {
// Only show error if it's not a validation error
        if (error.status !== 400) {
          toast.error(error.message || "Search failed");
        }
      } finally {
        setLoading(false);
      }
    },
    [conversationId]
  );

  // Handle input change with debouncing
  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Debounce search by 500ms
    const timeout = setTimeout(() => {
      performSearch(value, 1);
    }, 500);

    setSearchTimeout(timeout);
  };

  // Handle clicking on a search result
  const handleResultClick = (result) => {
    if (!result.messageId || !result.conversationId) {
      toast.error("Cannot navigate to this message");
      return;
    }
// Show loading state
    setLoading(true);

    // Call the parent's jump handler
    onJumpToMessage(result.conversationId, result.messageId);
    
    // Close modal after a short delay to show loading state briefly
    setTimeout(() => {
      onClose();
      setLoading(false);
    }, 300);
  };

  // Load more results
  const loadMoreResults = () => {
    if (!loading && pagination.hasNext) {
      performSearch(query, pagination.currentPage + 1);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Highlight query in text
  const highlightText = (text, query) => {
    if (!query.trim()) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} className="msgx-search-highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="msgx-search-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="msgx-search-modal"
          initial={{ scale: 0.9, opacity: 0, y: -20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: -20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="msgx-search-modal-header">
            <div className="msgx-search-input-wrapper">
              <FiSearch className="msgx-search-icon" />
              <input
                type="text"
                className="msgx-search-input"
                placeholder={
                  conversationName
                    ? `Search in ${conversationName}...`
                    : "Search messages..."
                }
                value={query}
                onChange={handleQueryChange}
                autoFocus
              />
              {query && (
                <button
                  className="msgx-search-clear-btn"
                  onClick={() => {
                    setQuery("");
                    setResults([]);
                  }}
                >
                  <FiX />
                </button>
              )}
            </div>
            <button className="msgx-search-close-btn" onClick={onClose}>
              <FiX />
            </button>
          </div>

          {/* Results */}
          <div className="msgx-search-results">
            {/* Loading overlay when navigating to message */}
            {loading && results.length > 0 && (
              <div className="msgx-search-loading" style={{
                position: 'absolute',
                top: '60px',
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backdropFilter: 'blur(4px)'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div className="msgx-spinner" />
                  <span style={{ display: 'block', marginTop: '12px', fontSize: '14px', color: '#666' }}>Jumping to message...</span>
                </div>
              </div>
            )}
            {loading && results.length === 0 ? (
              <div className="msgx-search-loading">
                <div className="msgx-spinner" />
                <span>Searching...</span>
              </div>
            ) : results.length > 0 ? (
              <>
                <div className="msgx-search-results-header">
                  <span>
                    {pagination.currentPage === 1 && results.length < 20
                      ? `${results.length} result${results.length !== 1 ? "s" : ""}`
                      : `Showing ${results.length} results`}
                  </span>
                </div>
                <div className="msgx-search-results-list">
                  {results.map((result) => (
                    <motion.div
                      key={result._id}
                      className="msgx-search-result-item"
                      whileHover={{ backgroundColor: "rgba(0, 0, 0, 0.03)" }}
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="msgx-search-result-avatar">
                        {result.sender?.profilePhotoUrl ? (
                          <img
                            src={result.sender.profilePhotoUrl}
                            alt={result.sender.name}
                          />
                        ) : (
                          <div className="msgx-search-result-avatar-text">
                            {result.sender?.name?.charAt(0).toUpperCase() || "?"}
                          </div>
                        )}
                      </div>
                      <div className="msgx-search-result-content">
                        <div className="msgx-search-result-header">
                          <span className="msgx-search-result-name">
                            {result.conversation?.otherParticipant?.name ||
                              result.sender?.name ||
                              "Unknown"}
                          </span>
                          <span className="msgx-search-result-time">
                            <FiClock size={12} />
                            {formatDate(result.createdAt)}
                          </span>
                        </div>
                        <div className="msgx-search-result-message">
                          {(() => {
const displayText = result.isDeleted 
                              ? "[Deleted message]"
                              : result.content || "[No content available]";
const highlighted = highlightText(displayText, query);
return highlighted;
                          })()}
                        </div>
                      </div>
                      <FiArrowRight className="msgx-search-result-arrow" />
                    </motion.div>
                  ))}

                  {/* Load More */}
                  {pagination.hasNext && (
                    <button
                      className="msgx-search-load-more"
                      onClick={loadMoreResults}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Load more results"}
                    </button>
                  )}
                </div>
              </>
            ) : query.trim() ? (
              query.trim().length < 2 ? (
                <div className="msgx-search-empty">
                  <FiSearch size={48} />
                  <h3>Type at least 2 characters</h3>
                  <p>Enter more characters to search</p>
                </div>
              ) : (
                <div className="msgx-search-empty">
                  <FiMessageCircle size={48} />
                  <h3>No messages found</h3>
                  <p>Try a different search term</p>
                </div>
              )
            ) : (
              <div className="msgx-search-empty">
                <FiSearch size={48} />
                <h3>Search Messages</h3>
                <p>
                  {conversationName
                    ? `Find messages in ${conversationName}`
                    : "Search across all your conversations"}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MessageSearchModal;
